/**
 * Ce que la trousse contient. PUR : une liste d'entrées, l'ordre, et celle qu'on met en avant.
 *
 * La trousse remplace la barre d'icônes de l'exercice « sur la feuille » : on y trouve les outils de
 * la feuille (tableau à dresser, arbre, avancement, corrigé) ET les icônes du player (Cours,
 * Calculatrice, Boîte à outils, Défi, Me filmer) et les repères de l'énoncé (figure, tableau).
 */
import type { LucideIcon } from 'lucide-react';

export type TrousseGroup =
  /** les Moves : ce qu'on sort pour avancer — une connaissance (or) ou un geste (violet) */
  | 'moves'
  /** ce que la feuille attend ou propose : tableau, arbre, avancement, corrigé */
  | 'sheet'
  /** aide pendant l'exercice : cours, calculatrice, boîte à outils */
  | 'help'
  /** se repérer dans l'énoncé : figure, tableau, graphique */
  | 'anchor'
  /** partager : défi, se filmer */
  | 'share';

export interface TrousseItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** un signe dessiné à la place de l'icône (⊞ tableau de signes, ⑂ arbre…) */
  mark?: string;
  group: TrousseGroup;
  /** l'outil que l'exercice attend : en or, et la trousse appelle */
  highlight?: boolean;
  /** vrai quand l'outil est déjà en cours (caméra allumée) */
  active?: boolean;
  /** pas encore acquis : l'entrée est là, en attendant (un Move qu'on n'a pas débloqué) */
  pending?: boolean;
  /** la couleur du Move : une connaissance est en or, un geste en violet */
  tone?: 'savoir' | 'geste';
  /** l'onglet de la trousse ; par défaut : les Moves dans « Moves », le cours dans « Cours », le reste dans « Outils » */
  tab?: TrousseTab;
  /** une phrase sous le nom (une connaissance acquise rappelle son énoncé dans « Cours ») */
  note?: string;
  /** rien à faire en la touchant : une fiche à lire (connaissance acquise, rappelée dans « Cours ») */
  readOnly?: boolean;
  onPick: () => void;
}

/** Les trois onglets de la trousse ouverte (retour de Philippe du 5/11/2026). */
export type TrousseTab = 'moves' | 'cours' | 'outils';

export const TROUSSE_TABS: { id: TrousseTab; label: string; empty: string }[] = [
  {
    id: 'moves',
    label: 'Moves',
    empty: 'Pas encore de Move ici : ils arrivent quand la question en demande un.',
  },
  { id: 'cours', label: 'Cours', empty: 'Pas de cours pour cette question.' },
  { id: 'outils', label: 'Outils', empty: 'Pas d’outil pour cette question.' },
];

/** L'onglet d'une entrée. PUR. */
export function trousseTabOf(it: TrousseItem): TrousseTab {
  if (it.tab) return it.tab;
  if (it.group === 'moves') return 'moves';
  if (it.id === 'lesson') return 'cours';
  return 'outils';
}

/**
 * L'onglet ouvert par défaut : celui de l'entrée que l'exercice attend (en or), sinon les Moves s'il y
 * en a, sinon le premier onglet qui a quelque chose. PUR.
 */
export function defaultTrousseTab(items: TrousseItem[]): TrousseTab {
  const call = items.find((it) => it.highlight);
  if (call) return trousseTabOf(call);
  for (const t of TROUSSE_TABS) if (items.some((it) => trousseTabOf(it) === t.id)) return t.id;
  return 'moves';
}

const ORDER: TrousseGroup[] = ['moves', 'sheet', 'help', 'anchor', 'share'];

/** L'ordre d'affichage : les Moves d'abord, puis ce que la feuille attend, le partage en dernier. */
export function sortTrousseItems(items: TrousseItem[]): TrousseItem[] {
  return items
    .map((it, i) => ({ it, i }))
    .sort((a, b) => {
      const g = ORDER.indexOf(a.it.group) - ORDER.indexOf(b.it.group);
      if (g) return g;
      // à groupe égal, l'outil attendu passe devant
      const h = Number(!!b.it.highlight) - Number(!!a.it.highlight);
      return h || a.i - b.i;
    })
    .map(({ it }) => it);
}

/** La trousse appelle (pastille dorée) quand l'exercice attend un de ses outils. */
export function trousseCalls(items: TrousseItem[]): boolean {
  return items.some((it) => it.highlight);
}

/** Ce que le lecteur d'écran annonce : le nom de la trousse, et ce qu'elle réclame. */
export function trousseAria(name: string, items: TrousseItem[]): string {
  const call = items.find((it) => it.highlight);
  return call ? `Ma trousse — ${name} : ${call.label} à faire` : `Ma trousse — ${name}`;
}
